import { Button, Card, Checkbox, Col, Divider, Empty, Form, Input, Row, Typography } from "antd";
import useEmailExtraFileCrud from "../hooks/useEmailExtraFileCrud";
import UploadInput from "components/UploadInput";
import { useTranslation } from "react-i18next";
import ExtraFileItem from "./ExtraFileItem";
import React from "react";

const DOCUMENT_TYPE_FIELDS = [
  { key: "isActiveBudget", label: "Budget" },
  { key: "isActiveSaleOrder", label: "Sale order" },
  { key: "isActiveDeliveryForm", label: "Delivery form" },
  { key: "isActivePurchaseOrder", label: "Purchase order" },
  { key: "isActiveReturn", label: "Return" },
  { key: "isActiveReceipt", label: "Receipt" },
  { key: "isActivePaymentInAdvance", label: "Payment in advance" },
] as const;

export default function EmailAttachments(): React.ReactElement {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const { extraFiles, createLoading, handleCreate, handleUpdate, handleDelete } =
    useEmailExtraFileCrud(form);

  return (
    <Card className="antd-newrai-card">
      <Typography.Title level={5}>{t("Email attachments")}</Typography.Title>
      <Typography.Text type="secondary" className="email-attachments__description">
        {t(
          "Upload files that will be automatically attached to emails. Use the checkboxes to select which document types include each attachment.",
        )}
      </Typography.Text>

      {extraFiles.length === 0 ? (
        <Empty description={t("No attachments")} style={{ marginBottom: 24 }} />
      ) : (
        extraFiles.map(item => (
          <ExtraFileItem
            key={item.id}
            item={item}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
          />
        ))
      )}

      <Divider />
      <br />
      <Typography.Title level={5}>{t("Add new attachment")}</Typography.Title>
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
        onValuesChange={changedValues => {
          if (changedValues.file?.file?.name) {
            form.setFieldsValue({ label: changedValues.file.file.name });
          }
        }}
      >
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={6}>
            <Form.Item
              name="file"
              label={t("File")}
              rules={[{ required: true, message: t("Please upload a file") }]}
            >
              <UploadInput alt="attachment" accept=".pdf" listType="text" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24}>
            <Row gutter={[0, 0]}>
              <Col xs={24} sm={6}>
                <Form.Item name="label" label={t("Label")}>
                  <Input placeholder={t("Optional label for this attachment")} />
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>
        <div className="email-attachments__checkboxes">
          {DOCUMENT_TYPE_FIELDS.map(({ key, label }) => (
            <Form.Item key={key} name={key} valuePropName="checked" noStyle>
              <Checkbox>{t(label)}</Checkbox>
            </Form.Item>
          ))}
        </div>
        <div className="email-attachments__actions">
          <Button type="primary" onClick={handleCreate} loading={createLoading}>
            {t("Save")}
          </Button>
        </div>
      </Form>
    </Card>
  );
}
